// src/components/Dashboard/RoleDashboard/PostOpportunity.jsx
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { apiFetch } from "../../../apiFetch";
import { resolveId } from './roleApi';
import './PostOpportunity.css';

const VALID_ROLES = ['agent', 'scout', 'club'];

const isDev =
    typeof process !== 'undefined'
        ? process.env.NODE_ENV !== 'production'
        : true;

// Displayed price (UI only)
const POST_PRICE_USD = 50;

/* -----------------------------------------------------
   Shared hook: resolve role + id (same pattern as RoleProfile)
------------------------------------------------------*/
function useRoleAndId(props) {
    const params = useParams();
    let role = (props?.role || params.role || 'agent').toLowerCase();
    if (!VALID_ROLES.includes(role)) role = 'agent';

    const rawId = props?.id || params.id || params.userId;
    const id = resolveId({
        role,
        id: rawId,
        agentId: props?.agentId,
        scoutId: props?.scoutId,
        clubId: props?.clubId,
        userId: props?.userId,
    });

    if (isDev) {
        console.debug('[PostOpportunity] role/id', {
            role,
            rawId,
            resolved: id,
            params,
        });
    }

    return { role, id };
}

/* -----------------------------------------------------
   Component
------------------------------------------------------*/
const PostOpportunity = (props) => {
    const { source } = props;
    const { role, id } = useRoleAndId(props);

    const [description, setDescription] = useState('');
    const [link, setLink] = useState('');
    const [countries, setCountries] = useState([]);
    const [countriesLoading, setCountriesLoading] = useState(true);
    const [posting, setPosting] = useState(false);
    const [showBetaModal, setShowBetaModal] = useState(false);
    const [filters, setFilters] = useState({
        country: '',
        ageRange: '',
        generalPosition: '',
        specificPosition: '',
    });

    const ageRanges = [
        '13-16',
        '17-20',
        '21-24',
        '25-30',
        '31-35',
        '36-40',
        '41-50',
        '50+',
    ];
    const positions = [
        'Goal Keepers',
        'Defenders',
        'Midfielders',
        'Forwards/Attackers',
    ];
    const specificPositions = [
        'GK',
        'LB',
        'RB',
        'CB',
        'CDM',
        'CM',
        'AM',
        'LW',
        'RW',
        'ST',
    ];

    // Viewer role – same logic as RoleProfile (who is logged in)
    const appViewerRole =
        typeof window !== 'undefined'
            ? (localStorage.getItem('userRole') || '').toLowerCase()
            : '';

    const canPost = appViewerRole === role;
    const roleLabel = role.charAt(0).toUpperCase() + role.slice(1);

    /* -----------------------------------------------------
       Fetch countries (same as your original logic)
    ------------------------------------------------------*/
    useEffect(() => {
        const fetchCountries = async () => {
            try {
                const response = await apiFetch("/countries");
                const data = await response.json();
                if (Array.isArray(data.countries)) {
                    const sorted = data.countries.sort((a, b) => a.localeCompare(b));
                    setCountries(sorted);
                } else {
                    console.error('Unexpected response structure:', data);
                    setCountries([]);
                }
            } catch (error) {
                console.error('Error fetching countries from custom API:', error);
                setCountries([]);
            } finally {
                setCountriesLoading(false);
            }
        };

        fetchCountries();
    }, []);

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters((prev) => ({ ...prev, [name]: value }));
    };

    /* -----------------------------------------------------
       Handle post – multi-role, hits /opportunities
       UPDATED: Create opportunity then redirect to Stripe Checkout
    ------------------------------------------------------*/
    const handlePost = async () => {
        setShowBetaModal(true);
        return;

        if (!role || !id) {
            alert('❌ Role or profile ID not identified. Please log in again.');
            return;
        }

        if (!canPost) {
            alert(
                `You are logged in as "${appViewerRole || 'unknown'}" and cannot post opportunities for this ${roleLabel}.`
            );
            return;
        }

        if (description.trim() === '') {
            alert('Please enter the opportunity description.');
            return;
        }

        // Optional confirmation to prevent surprise payment
        const ok = window.confirm(
            `Posting an opportunity costs $${POST_PRICE_USD} (one-time fee). Continue to payment?`
        );
        if (!ok) return;

        try {
            setPosting(true);

            const payload = {
                ownerRole: role,
                ownerId: id,
                agentId: role === "agent" ? id : null,
                description,
                link,
                source: source || roleLabel,
                filters,
            };

            // 1) Create opportunity (backend will set status = PendingPayment)
            const res = await apiFetch("/api/opportunities", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });

            if (!res.ok) {
                const text = await res.text().catch(() => "");
                throw new Error(`Post opportunity failed (${res.status}): ${text.slice(0, 200)}`);
            }

            const created = await res.json().catch(() => null);
            const opportunityId = created?.id;

            if (!opportunityId) {
                throw new Error("Opportunity created but missing id in response.");
            }

            // 2) Start Stripe checkout for this opportunity
            const checkoutRes = await apiFetch("/api/checkout/post-opportunity", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ opportunityId }),
            });

            if (!checkoutRes.ok) {
                const text = await checkoutRes.text().catch(() => "");
                throw new Error(`Checkout failed (${checkoutRes.status}): ${text.slice(0, 200)}`);
            }

            const { url } = await checkoutRes.json().catch(() => ({}));

            if (!url) {
                throw new Error("Checkout session created but missing redirect URL.");
            }

            // 3) Redirect to Stripe (Stripe handles payment UI)
            window.location.href = url;
        } catch (error) {
            console.error('❌ Post/checkout error:', error);
            alert(error?.message || 'Failed to start payment for posting.');
            setPosting(false);
        }
    };

    if (!role || !id) {
        return (
            <div className="post-opportunity">
                <div className="post-opportunity-title">Post Opportunity</div>
                <p style={{ color: 'red' }}>Missing role or id.</p>
            </div>
        );
    }

    return (
        <div className="post-opportunity">
            <div className="post-opportunity-title">
                Post Opportunity and Players will make Application
            </div>

            {!canPost && (
                <div className="post-opportunity-banner warning">
                    You are logged in as <strong>{appViewerRole || 'guest'}</strong>, so you cannot
                    post opportunities for this {roleLabel.toLowerCase()}.
                </div>
            )}

            <div className="link-description-container">
                <textarea
                    id="describe-opportunity"
                    placeholder="Enter opportunity description (specify gender, city, etc. if applicable)"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    disabled={!canPost || posting}
                />
                <input
                    type="text"
                    placeholder="Paste link here"
                    value={link}
                    onChange={(e) => setLink(e.target.value)}
                    disabled={!canPost || posting}
                />
            </div>

            <div className="filters-container">
                <div className="filter-group">
                    <label>Location</label>
                    <select
                        name="country"
                        value={filters.country}
                        onChange={handleFilterChange}
                        disabled={!canPost || posting}
                    >
                        <option value="">Select Country</option>
                        {countriesLoading ? (
                            <option>Loading...</option>
                        ) : (
                            countries.map((country, index) => (
                                <option key={index} value={country}>
                                    {country}
                                </option>
                            ))
                        )}
                    </select>
                </div>

                <div className="filter-group">
                    <label>Age Range</label>
                    <select
                        name="ageRange"
                        value={filters.ageRange}
                        onChange={handleFilterChange}
                        disabled={!canPost || posting}
                    >
                        <option value="">Select Age Range</option>
                        {ageRanges.map((age, index) => (
                            <option key={index} value={age}>
                                {age}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="filter-group">
                    <label>General Position</label>
                    <select
                        name="generalPosition"
                        value={filters.generalPosition}
                        onChange={handleFilterChange}
                        disabled={!canPost || posting}
                    >
                        <option value="">Select Position</option>
                        {positions.map((pos, index) => (
                            <option key={index} value={pos}>
                                {pos}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="filter-group">
                    <label>Specific Position</label>
                    <select
                        name="specificPosition"
                        value={filters.specificPosition}
                        onChange={handleFilterChange}
                        disabled={!canPost || posting}
                    >
                        <option value="">Select Specific Position</option>
                        {specificPositions.map((pos, index) => (
                            <option key={index} value={pos}>
                                {pos}
                            </option>
                        ))}
                    </select>
                </div>

                {/* ✅ Updated button section: price shows on hover ABOVE the button */}
                <div className="post-button-container" style={{ position: "relative" }}>
                    <label>&nbsp;</label>

                    <button
                        onClick={handlePost}
                        disabled={!canPost || posting || description.trim() === ''}
                        style={{ whiteSpace: "nowrap", minWidth: 110 }}
                        title="Posting feature coming soon"
                    >
                        {posting ? (
                            <>
                                <Loader2
                                    className="animate-spin"
                                    style={{ width: 16, height: 16, verticalAlign: 'middle' }}
                                />{' '}
                                Processing...
                            </>
                        ) : (
                            'Post'
                        )}
                    </button>
                </div>
            </div>

            {showBetaModal && (
                <div className="player-profile-underage-modal-overlay">
                    <div
                        className="player-profile-underage-modal"
                        style={{
                            backgroundColor: 'black',
                            border: '2px solid yellow',
                            borderRadius: '8px',
                            textAlign: 'center',
                            padding: '20px',
                            maxWidth: '420px',
                            margin: '0 auto',
                        }}
                    >
                        <h2 style={{ color: 'yellow', fontSize: '20px' }}>
                            🚧 Feature Not Available
                        </h2>

                        <p style={{ color: 'white', fontSize: '15px', marginTop: '12px' }}>
                            Posting opportunities is currently unavailable.
                            <br /><br />
                            The app is in <strong>Beta Testing Mode</strong>.
                        </p>

                        <button
                            onClick={() => setShowBetaModal(false)}
                            style={{
                                marginTop: '20px',
                                padding: '10px 40px',
                                backgroundColor: 'yellow',
                                color: 'black',
                                fontWeight: 'bold',
                                border: 'none',
                                borderRadius: '5px',
                                cursor: 'pointer',
                            }}
                        >
                            Close
                        </button>
                    </div>
                </div>
            )}

        </div>
    );
};

export default PostOpportunity;



